import { useEffect, useRef, useState } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { Loader2, Pencil, Check, X } from 'lucide-react'
import { useResetOnChange } from '../../hooks/useResetOnChange'
import { useProfileEditor } from '../../hooks/useProfileEditor'
import { classifyError } from '../../lib/errors'
import { saveButtonLabel, cancelButtonLabel } from '../../lib/commonStrings'
import { cn } from '../../lib/utils'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import type { UserProfile } from '../../types'

interface Props {
  uid: string | null
  loadedProfile: UserProfile | undefined
}

export function DisplayNameEditor({ uid, loadedProfile }: Props) {
  const { saveDisplayName } = useProfileEditor(uid)
  const [savingName, setSavingName] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [editingName, setEditingName] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  // Seed the editable name once the profile lands, and re-seed if the signed-in
  // identity changes underneath us. Not keyed on displayName: a rename that
  // round-trips through Firestore must not clobber an in-progress edit.
  const nameInitialized = Boolean(loadedProfile)
  const [draftName, setDraftName] = useResetOnChange(
    loadedProfile?.uid,
    () => loadedProfile?.displayName ?? '',
  )

  useEffect(() => {
    if (editingName) {
      nameInputRef.current?.focus()
      nameInputRef.current?.select()
    }
  }, [editingName])

  const trimmedDraft = draftName.trim()
  const currentName = (loadedProfile?.displayName ?? '').trim()
  const nameChanged = nameInitialized && trimmedDraft !== currentName
  const nameValid = trimmedDraft.length >= 1 && trimmedDraft.length <= 100

  async function handleSaveName(): Promise<boolean> {
    if (!uid || !nameChanged || !nameValid) return false
    setSavingName(true)
    setNameError(null)
    try {
      await saveDisplayName(trimmedDraft)
      toast.success(
        fbt('Display name updated', 'Toast confirmation after the user saves a new display name').toString(),
      )
      return true
    } catch (err) {
      classifyError(err)
      setNameError(
        fbt('Failed to update. Please try again.', 'Error message when saving the display name fails'),
      )
      return false
    } finally {
      setSavingName(false)
    }
  }

  function startEditingName() {
    setDraftName(currentName)
    setNameError(null)
    setEditingName(true)
  }

  function cancelEditingName() {
    setDraftName(currentName)
    setNameError(null)
    setEditingName(false)
  }

  async function commitName() {
    if (savingName) return
    if (!nameChanged) {
      cancelEditingName()
      return
    }
    if (!nameValid) {
      cancelEditingName()
      return
    }
    const ok = await handleSaveName()
    if (ok) setEditingName(false)
  }

  return (
    <>
      {editingName ? (
        <div className="flex w-full items-center gap-2">
          <Input
            ref={nameInputRef}
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
            maxLength={100}
            disabled={savingName}
            placeholder={fbt(
              'Your name',
              'Placeholder shown inside the display name input on the Profile page',
            ).toString()}
            aria-label={fbt(
              'Display name',
              'Aria label for the display name input on the Profile page',
            ).toString()}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitName()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                cancelEditingName()
              }
            }}
            className="h-9 min-w-0 flex-1 text-lg font-semibold"
          />
          <Button
            size="icon"
            className="size-9 cursor-pointer"
            disabled={!nameChanged || !nameValid || savingName}
            onClick={commitName}
            aria-label={saveButtonLabel()}
          >
            {savingName ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" />
            )}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-9 cursor-pointer text-muted-foreground"
            disabled={savingName}
            onClick={cancelEditingName}
            aria-label={cancelButtonLabel()}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={startEditingName}
          className="group inline-flex max-w-full items-center gap-1.5 cursor-pointer"
          aria-label={fbt(
            'Edit display name',
            'Accessible label for the button that edits the display name',
          ).toString()}
        >
          <h2
            className={cn(
              'truncate text-xl font-semibold',
              currentName ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {currentName || (
              <fbt desc="Placeholder heading shown when the user has not set a display name">Add your name</fbt>
            )}
          </h2>
          <Pencil className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
        </button>
      )}
      {nameError && <p className="mt-2 text-sm text-destructive">{nameError}</p>}
    </>
  )
}
